import {i18_TRANSLATION} from "../../../i18nReferences";

/** The pause before "send the code again" appears, so the mail has time to arrive. */
export const RESEND_DELAY_MS = 30 * 1000
export const DEFAULT_CODE_VALID_MINUTES = 10

const CODE_FORMAT = /^\d{6}$/

/** The code as typed or pasted ("123 456", " 123456 ") without any whitespace. */
export const normalizeCode = (input) => String(input ?? "").replace(/\s+/g, "")

export const isCompleteCode = (input) => CODE_FORMAT.test(normalizeCode(input))

/** Minutes the code is valid, from the answer of the challenge (`validForSeconds`). */
export const getCodeValidMinutes = (validForSeconds) =>
    Number.isFinite(validForSeconds) && validForSeconds > 0
        ? Math.max(1, Math.ceil(validForSeconds / 60))
        : DEFAULT_CODE_VALID_MINUTES

export const STEP_CHALLENGE = "challenge"
export const STEP_CONFIRM = "confirm"

export const PROBLEM_CODE_EXPIRED = "CODE_EXPIRED"

const PROBLEM_INVALID_CODE = "INVALID_CODE"
const PROBLEM_TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS"
const PROBLEM_UNAVAILABLE = "UNAVAILABLE"
const PROBLEM_GENERIC = "GENERIC"

const PROBLEM_TEXTS = {
    [PROBLEM_INVALID_CODE]: {key: "DEVICE_UNLOCK.ERROR_INVALID_CODE", defaultValue: "Der Code ist nicht korrekt."},
    [PROBLEM_CODE_EXPIRED]: {
        key: "DEVICE_UNLOCK.ERROR_CODE_EXPIRED",
        defaultValue: "Der Code ist abgelaufen. Bitte einen neuen Code anfordern.",
    },
    [PROBLEM_TOO_MANY_REQUESTS]: {
        key: "DEVICE_UNLOCK.ERROR_TOO_MANY_REQUESTS",
        defaultValue: "Es wurden zu viele Codes angefordert. Bitte später erneut versuchen.",
    },
    [PROBLEM_UNAVAILABLE]: {
        key: "DEVICE_UNLOCK.ERROR_UNAVAILABLE",
        defaultValue: "Der Code kann gerade nicht verschickt werden. Bitte später erneut versuchen.",
    },
    [PROBLEM_GENERIC]: {
        key: "DEVICE_UNLOCK.ERROR_GENERIC",
        defaultValue: "Das hat nicht geklappt. Bitte die Seite neu laden und erneut versuchen.",
    },
}

// The `reason` values of the backend, each mapped to one of the five problems. Rare causes (the artist has left,
// no login address, no mail possible) share the text "unavailable".
const PROBLEM_BY_REASON = {
    INVALID_CODE: PROBLEM_INVALID_CODE,
    CODE_EXPIRED: PROBLEM_CODE_EXPIRED,
    CODE_UNAVAILABLE: PROBLEM_UNAVAILABLE,
    NO_LOGIN_ADDRESS: PROBLEM_UNAVAILABLE,
    ARTIST_LEFT: PROBLEM_UNAVAILABLE,
}

/**
 * What went wrong with asking for or entering the code, as a name of `PROBLEM_TEXTS`. The backend names the
 * problem in `reason` (403 and 422 and 503); 429, 409 and 400 are told by their status, and a 400 means
 * something else for each step (no device cookie when asking, a malformed code when entering). Everything
 * unknown, 409 (already unlocked) included, is the generic problem, whose text asks to reload the page.
 */
export const getDeviceUnlockProblem = (error, step) => {
    const status = error?.response?.status
    const problemOfReason = PROBLEM_BY_REASON[error?.response?.data?.reason]
    if (problemOfReason) {
        return problemOfReason
    }
    if (status === 429) {
        return PROBLEM_TOO_MANY_REQUESTS
    }
    if (status === 400) {
        return step === STEP_CHALLENGE ? PROBLEM_UNAVAILABLE : PROBLEM_INVALID_CODE
    }
    return PROBLEM_GENERIC
}

export const getDeviceUnlockProblemText = (problem, t) => {
    const {key, defaultValue} = PROBLEM_TEXTS[problem] || PROBLEM_TEXTS[PROBLEM_GENERIC]
    return t(key, {ns: i18_TRANSLATION, defaultValue})
}
